import { useState, useEffect, useCallback, createContext, useContext } from "react";

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export const BREAKPOINTS: { name: Breakpoint; minWidth: number }[] = [
  { name: "2xl", minWidth: 1536 },
  { name: "xl", minWidth: 1280 },
  { name: "lg", minWidth: 1024 },
  { name: "md", minWidth: 768 },
  { name: "sm", minWidth: 640 },
  { name: "xs", minWidth: 0 },
]; // descending order — first match wins

export function getBreakpointFromWidth(width: number): Breakpoint {
  for (const { name, minWidth } of BREAKPOINTS) {
    if (width >= minWidth) return name;
  }
  return "xs";
}

export function resolveBreakpoint(): Breakpoint {
  if (typeof window === "undefined") return "xs";
  for (const { name, minWidth } of BREAKPOINTS) {
    if (window.matchMedia(`(min-width: ${minWidth}px)`).matches) return name;
  }
  return "xs";
}

export interface UseBreakpointReturn {
  /** The currently active Tailwind breakpoint (e.g. "md") */
  breakpoint: Breakpoint;
  /** Returns true when the viewport is at least `bp` */
  isMin: (bp: Breakpoint) => boolean;
  /** Returns true when the viewport is below `bp` */
  isBelow: (bp: Breakpoint) => boolean;
  /** Convenience boolean helpers */
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  is2Xl: boolean;
}

export const BP_ORDER: Breakpoint[] = ["xs", "sm", "md", "lg", "xl", "2xl"];

/**
 * Context populated by BreakpointProvider. When the provider is present,
 * useBreakpoint reads from here instead of registering its own listeners.
 */
export const BreakpointContext = createContext<UseBreakpointReturn | null>(null);

export function useBreakpoint(): UseBreakpointReturn {
  const ctx = useContext(BreakpointContext);
  const hasProvider = ctx !== null;

  // Standalone state — only drives the return value when no provider is mounted.
  // useState/useEffect must always be called (Rules of Hooks).
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(resolveBreakpoint);

  useEffect(() => {
    if (hasProvider || typeof window === "undefined") return;

    // One MQ list per boundary — only fires when a boundary is crossed
    const queries = BREAKPOINTS.filter(({ minWidth }) => minWidth > 0).map(({ name, minWidth }) => ({
      name,
      mql: window.matchMedia(`(min-width: ${minWidth}px)`),
    }));

    const handleChange = () => {
      const next = resolveBreakpoint();
      setBreakpoint((prev) => (prev === next ? prev : next));
    };

    queries.forEach(({ mql }) => mql.addEventListener("change", handleChange));

    return () => {
      queries.forEach(({ mql }) => mql.removeEventListener("change", handleChange));
    };
  }, [hasProvider]);

  // When a provider is present, derive from its breakpoint; otherwise use local state.
  const active = ctx?.breakpoint ?? breakpoint;

  const isMin = useCallback((bp: Breakpoint) => BP_ORDER.indexOf(active) >= BP_ORDER.indexOf(bp), [active]);
  const isBelow = useCallback((bp: Breakpoint) => BP_ORDER.indexOf(active) < BP_ORDER.indexOf(bp), [active]);

  // Fast path: return the context value directly (isMin/isBelow already computed above).
  if (ctx) return ctx;

  return {
    breakpoint: active,
    isMin,
    isBelow,
    isXs: true, // xs is always active (min-width: 0)
    isSm: isMin("sm"),
    isMd: isMin("md"),
    isLg: isMin("lg"),
    isXl: isMin("xl"),
    is2Xl: isMin("2xl"),
  };
}
